import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from '@/components/Button';
import Dialog, {
	DialogContent,
	DialogFooter,
	DialogTop
} from '@/components/Dialog';
import { useSubscriptionInfo } from '@/hooks/user';
import DeleteMethodIcon from '@/icons/delete-method.svg';
import Remove from '@/icons/delete-payment-method.svg';
import paymentMethodModel from '@/models/paymentMethod';
import { fetchBillingInfo } from '@/store/profile/thunks';
import { cn, showError } from '@/utils';

function RemovePaymentMethod({ open, onOpenChange }) {
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	const dispatch = useDispatch();

	const { cancellationRequested, isExpired, isCanceled } =
		useSubscriptionInfo();

	const handleRemovePaymentMethod = async () => {
		try {
			setLoading(true);
			await paymentMethodModel.removePaymentMethod();
			await dispatch(fetchBillingInfo()).unwrap();

			onOpenChange(false);
		} catch (err) {
			showError(err);
		}
		setLoading(false);
	};

	const canRemovePayment = cancellationRequested || isExpired || isCanceled;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="w-[280px] md:w-[397px]">
				<DialogTop
					title={t('billing.modals.removePaymentMethod.title')}
					description={t('billing.modals.removePaymentMethod.title')}
				/>

				<div className="flex flex-col items-center gap-2 px-4 py-9">
					{canRemovePayment ? <Remove /> : <DeleteMethodIcon />}
					<p className="max-w-[250px] whitespace-pre-line text-center text-sm text-grey-500">
						{canRemovePayment
							? t('billing.modals.removePaymentMethod.text')
							: t('billing.modals.removePaymentMethod.denied')}
					</p>
				</div>
				<DialogFooter
					className={cn(
						'border-t border-grey-200 px-4 pb-6 pt-4',
						canRemovePayment
							? 'grid w-full grid-cols-2 justify-center gap-4'
							: 'align-center flex'
					)}
				>
					{canRemovePayment && (
						<Button
							variant="outline"
							onClick={() => {
								onOpenChange(false);
							}}
						>
							{t('common.cancel')}
						</Button>
					)}
					<Button
						className={cn(!canRemovePayment && 'w-full')}
						onClick={() => {
							if (canRemovePayment) handleRemovePaymentMethod();
							else onOpenChange(false);
						}}
						loading={loading}
					>
						{t('common.accept')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export default RemovePaymentMethod;
